/**
 * JotRx Setup Callout
 *
 * A lightweight "next step" nudge shown at the top of settings pages.
 * Page-aware and state-aware — shows contextual instructions depending
 * on where the user is and what setup step they need to complete next.
 *
 * Works for both entitled and non-entitled users (the banner ad flow
 * sends non-entitled users here to start purchase/setup).
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import MedicationIcon from '@mui/icons-material/Medication';
import { alpha, Avatar, Box, Button, Paper, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { hasAdminPrivileges } from '@utils/roleUtils';

import { type JotRxSetupState, useJotRxSetupState } from '../hooks/useJotRxSetupState';

export type CalloutPage = 'practice-settings' | 'clinicians' | 'account-settings';

interface JotRxSetupCalloutProps {
  /** Which settings page this callout is on */
  page: CalloutPage;
  /** The user's group roles (for determining admin vs clinician flow) */
  userRoles?: string[];
  /** Optional: whether a setup action just completed */
  justCompleted?: boolean;
}

interface CalloutContent {
  label: string;
  message: string;
  detail?: string;
  action:
    | { type: 'scroll'; elementId: string }
    | { type: 'navigate'; route: string; buttonLabel: string };
  isSuccess: boolean;
  /** Use pencil icon instead of pill */
  usePencilIcon?: boolean;
}

export function JotRxSetupCallout({
  page,
  userRoles,
  justCompleted = false,
}: JotRxSetupCalloutProps) {
  const navigate = useNavigate();
  const { setupState, isJotRxEnabled, isJotRxPurchasable, isLoading, isFullySetup } = useJotRxSetupState();
  const { jotrx } = useEventTracking();
  const isAdmin = hasAdminPrivileges(userRoles);

  const content = useMemo(
    () => getCalloutContent(page, setupState, isAdmin, isJotRxEnabled, justCompleted),
    [page, setupState, isAdmin, isJotRxEnabled, justCompleted],
  );

  const isVisible = (isJotRxEnabled || isJotRxPurchasable) && !isFullySetup && !isLoading && !!content;

  useEffect(() => {
    if (isVisible) {
      jotrx.calloutShown({ page, setup_state: setupState });
    }
  }, [isVisible, jotrx, page, setupState]);

  const handleCtaClick = useCallback(() => {
    if (!content) return;
    jotrx.calloutCtaClicked({ page, setup_state: setupState });

    if (content.action.type === 'scroll') {
      const el = document.getElementById(content.action.elementId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(content.action.route);
    }
  }, [content, jotrx, page, setupState, navigate]);

  if (!isVisible || !content) return null;

  // Success / step-completed variant
  if (content.isSuccess) {
    return (
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderColor: 'success.light',
          bgcolor: theme => alpha(theme.palette.success.main, 0.05),
          borderRadius: 2,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {content.label}
          </Box>
          {' — '}
          {content.message}
        </Typography>
        {content.action.type === 'navigate' && (
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: '12px !important' }} />}
            onClick={handleCtaClick}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: 'success.main',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Continue
          </Button>
        )}
      </Paper>
    );
  }

  // Default info variant
  const isScrollAction = content.action.type === 'scroll';
  const IconComponent = content.usePencilIcon ? EditIcon : MedicationIcon;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        borderColor: theme => alpha(theme.palette.primary.main, 0.2),
        bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
        borderRadius: 2,
      }}
    >
      {/* Icon */}
      <Avatar
        sx={{
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          width: 32,
          height: 32,
          flexShrink: 0,
        }}
      >
        <IconComponent sx={{ fontSize: 16 }} />
      </Avatar>

      {/* Copy */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'primary.main', display: 'block', lineHeight: 1.4 }}
        >
          {content.label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', mt: 0.25, lineHeight: 1.4 }}
        >
          {content.message}
        </Typography>
        {content.detail && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              display: 'block',
              mt: 0.25,
              fontSize: '0.65rem',
              lineHeight: 1.4,
            }}
          >
            {content.detail}
          </Typography>
        )}
      </Box>

      {/* CTA */}
      {content.action.type === 'scroll' ? (
        <Box
          onClick={handleCtaClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Below
          </Typography>
        </Box>
      ) : (
        <Button
          size="small"
          variant="contained"
          endIcon={<ArrowForwardIcon sx={{ fontSize: '12px !important' }} />}
          onClick={handleCtaClick}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.7rem',
            borderRadius: 1.5,
            px: 1.5,
            py: 0.5,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            boxShadow: 1,
          }}
        >
          {content.action.buttonLabel}
        </Button>
      )}
    </Paper>
  );
}

/**
 * Determines what the callout should say based on the current page,
 * setup state, and user role. Returns null if nothing should show.
 */
function getCalloutContent(
  page: CalloutPage,
  state: JotRxSetupState,
  isAdmin: boolean,
  hasEntitlement: boolean,
  justCompleted: boolean,
): CalloutContent | null {
  // ---------- Clinicians ----------
  if (page === 'clinicians') {
    if (justCompleted && state === 'practice_connected') {
      return {
        label: 'JotRx added!',
        message: 'Next: connect your practice to DoseSpot in Practice Settings.',
        action: { type: 'navigate', route: '/practice/settings', buttonLabel: 'Continue' },
        isSuccess: true,
      };
    }
    if (state === 'not_connected' && !hasEntitlement) {
      return {
        label: 'Add JotRx to your plan',
        message: 'Open the Actions menu next to your name to purchase the JotRx e-prescribing add-on.',
        action: { type: 'scroll', elementId: 'member-table' },
        isSuccess: false,
      };
    }
    if (state === 'not_connected' && hasEntitlement) {
      return {
        label: 'Connect to DoseSpot',
        message: 'Connect your practice to DoseSpot to enable e-prescribing.',
        action: {
          type: 'navigate',
          route: '/practice/settings',
          buttonLabel: 'Go to Practice Settings',
        },
        isSuccess: false,
      };
    }
    if (state === 'practice_connected' && !hasEntitlement) {
      return {
        label: 'Add JotRx to your plan',
        message: 'Open the Actions menu next to your name to purchase the JotRx e-prescribing add-on.',
        action: { type: 'scroll', elementId: 'member-table' },
        isSuccess: false,
      };
    }
    if (state === 'practice_connected' && hasEntitlement) {
      return {
        label: 'Register as a prescriber',
        message: 'Your practice is connected. Register in your Account Settings.',
        action: {
          type: 'navigate',
          route: '/user',
          buttonLabel: 'Go to Account Settings',
        },
        isSuccess: false,
      };
    }
    return null;
  }

  // ---------- Practice Settings ----------
  if (page === 'practice-settings') {
    if (justCompleted && state === 'practice_connected') {
      return {
        label: 'Practice connected!',
        message: 'Next: register as a prescriber in your Account Settings.',
        action: { type: 'navigate', route: '/user', buttonLabel: 'Continue' },
        isSuccess: true,
      };
    }
    if (state === 'not_connected' && !hasEntitlement) {
      // No entitlement yet — redirect to clinicians to purchase JotRx first
      return {
        label: 'Add JotRx to your plan',
        message: 'Purchase the JotRx add-on from the Clinicians page to enable e-prescribing.',
        action: {
          type: 'navigate',
          route: '/practice/clinicians',
          buttonLabel: 'Go to Clinicians',
        },
        isSuccess: false,
      };
    }
    if (state === 'not_connected' && hasEntitlement) {
      return {
        label: 'Connect to DoseSpot',
        message: 'Click "Connect to DoseSpot" below to link your clinic for e-prescribing.',
        detail: 'Scroll down to the E-Prescribing (JotRx) section.',
        action: { type: 'scroll', elementId: 'eprescription-org-section' },
        isSuccess: false,
      };
    }
    if (state === 'practice_connected' && !hasEntitlement) {
      return {
        label: 'Add JotRx to your plan',
        message: 'Purchase the JotRx add-on from the Clinicians page to enable e-prescribing.',
        action: {
          type: 'navigate',
          route: '/practice/clinicians',
          buttonLabel: 'Go to Clinicians',
        },
        isSuccess: false,
      };
    }
    if (state === 'practice_connected' && hasEntitlement) {
      return {
        label: 'Practice connected',
        message: 'Next: register as a prescriber in your Account Settings.',
        action: {
          type: 'navigate',
          route: '/user',
          buttonLabel: 'Go to Account Settings',
        },
        isSuccess: false,
      };
    }
    if (state === 'registered') {
      return {
        label: 'Almost done',
        message: 'Complete identity proofing in DoseSpot to start prescribing.',
        action: {
          type: 'navigate',
          route: '/user',
          buttonLabel: 'Go to Account Settings',
        },
        isSuccess: false,
      };
    }
    return null;
  }

  // ---------- Account Settings ----------
  if (page === 'account-settings') {
    if (justCompleted && state === 'registered') {
      return {
        label: 'Registration complete!',
        message: 'Open DoseSpot to finish identity proofing.',
        action: { type: 'navigate', route: '/user', buttonLabel: 'Continue' },
        isSuccess: true,
      };
    }
    if (state === 'not_connected' && !hasEntitlement) {
      return {
        label: 'Add JotRx to your plan',
        message: 'Purchase the JotRx add-on from the Clinicians page to enable e-prescribing.',
        action: {
          type: 'navigate',
          route: '/practice/clinicians',
          buttonLabel: 'Go to Clinicians',
        },
        isSuccess: false,
      };
    }
    if (state === 'not_connected' && hasEntitlement) {
      if (isAdmin) {
        return {
          label: 'Connect your practice first',
          message: 'Your practice needs to be connected to DoseSpot before you can register.',
          action: {
            type: 'navigate',
            route: '/practice/settings',
            buttonLabel: 'Go to Practice Settings',
          },
          isSuccess: false,
        };
      }
      return {
        label: 'Connect your practice first',
        message: 'Ask your practice admin to connect to DoseSpot before you can register.',
        action: {
          type: 'navigate',
          route: '/practice/settings',
          buttonLabel: 'Go to Practice Settings',
        },
        isSuccess: false,
      };
    }
    if (state === 'practice_connected' && !hasEntitlement) {
      return {
        label: 'Add JotRx to your plan',
        message: 'Purchase the JotRx add-on from the Clinicians page to enable e-prescribing.',
        action: {
          type: 'navigate',
          route: '/practice/clinicians',
          buttonLabel: 'Go to Clinicians',
        },
        isSuccess: false,
      };
    }
    if (state === 'practice_connected' && hasEntitlement) {
      return {
        label: 'Register for e-prescribing',
        message: 'Click "Register for E-prescribing" below to get started.',
        detail: 'Scroll down to the E-Prescribing section.',
        action: { type: 'scroll', elementId: 'eprescription-user-section' },
        isSuccess: false,
      };
    }
    if (state === 'registered') {
      return {
        label: 'Complete identity proofing',
        message: "You're registered. Open DoseSpot to complete identity proofing.",
        action: { type: 'scroll', elementId: 'eprescription-user-section' },
        isSuccess: false,
      };
    }
    return null;
  }

  return null;
}
